/* eslint-disable react-refresh/only-export-components */

import { Suspense, lazy, useEffect, useMemo } from "react";
import { createBrowserRouter, useNavigate } from "react-router-dom";
import { NO_ACCESS_PATH, resolveLandingPath } from "./utils/landingPath";

import { Spin } from "antd";
import { jwtDecode } from "jwt-decode";
import AuthGroup from "../configs/authgroup";
import Permissions from "../configs/permission"; // Import permission constants
import AdminLayout from "./Components/Layout/Admin";
import SummaryReport from "./pages/bill/summaryreport/Index.jsx";
import SummaryReportDetail from "./pages/bill/summaryreport/detail/Index.jsx";
import LogoutSuccess from "./pages/callback/Logout";
import Success from "./pages/callback/Success";
import Home from "./pages/home/Index.jsx";
import Login from "./pages/login/Index.jsx";
import Logout from "./pages/logout/Index.jsx";

// หน้าอื่นทั้งหมดโหลดแบบ lazy (แยก chunk ต่อหน้า) — คง Login/Home/callback เป็น eager เพราะเป็น critical path ตอนเปิดแอป
// ผล: lib หนักของฝั่ง export (exceljs, @react-pdf/renderer) ย้ายไปอยู่ chunk ของหน้า bill/list ไม่ถ่วง first load ของ dashboard
const lazyPage = (loader, name) => lazy(() => loader().then((m) => ({ default: name ? m[name] : m.default })));

const CompanyBillCreatePage = lazyPage(
  () => import("./pages/bill/companybill/create/index.jsx"),
  "CompanyBillCreatePage"
);
const CompanyBillPage = lazyPage(() => import("./pages/bill/companybill/Index.jsx"), "CompanyBillPage");
const CustomerBilCreatePage = lazyPage(
  () => import("./pages/bill/customerbill/create/index.jsx"),
  "CustomerBilCreatePage"
);
const CustomerBilPage = lazyPage(() => import("./pages/bill/customerbill/Index.jsx"), "CustomerBilPage");
const CompanyCreatePage = lazyPage(() => import("./pages/company/create/index"), "CompanyCreatePage");
const CompanyPage = lazyPage(() => import("./pages/company/Index"), "CompanyPage");
const CustomerCreatePage = lazyPage(() => import("./pages/customer/create/index"), "CustomerCreatePage");
const CustomerPage = lazyPage(() => import("./pages/customer/Index"), "CustomerPage");
const NoAccessPage = lazyPage(() => import("./pages/noAccess/Index.jsx"));
const DashboardInboundCompanyDetailPage = lazyPage(
  () => import("./pages/home/inboundCompanyDetail/Index.jsx"),
  "DashboardInboundCompanyDetail"
);
const DashboardInboundSummaryCompanyPage = lazyPage(
  () => import("./pages/home/inboundSummaryCompany/Index.jsx"),
  "DashboardInboundSummaryCompany"
);
const DestinationDeliveryCreatePage = lazyPage(
  () => import("./pages/package/destinationdelivery/create/index.jsx"),
  "DestinationDeliveryCreatePage"
);
const DestinationDeliveryPage = lazyPage(
  () => import("./pages/package/destinationdelivery/Index.jsx"),
  "DestinationDeliveryPage"
);
const DestinationPackageCreatePage = lazyPage(
  () => import("./pages/package/destinationpackage/create/index.jsx"),
  "DestinationPackageCreatePage"
);
const DestinationPackagePage = lazyPage(
  () => import("./pages/package/destinationpackage/Index.jsx"),
  "DestinationPackagePage"
);
const DestinationPackageDeliveryCreatePage = lazyPage(
  () => import("./pages/package/destinationpackagedelivery/create/index.jsx"),
  "DestinationPackageDeliveryCreatePage"
);
const DestinationPackageDeliveryPage = lazyPage(
  () => import("./pages/package/destinationpackagedelivery/Index.jsx"),
  "DestinationPackageDeliveryPage"
);
const OriginPackageCreatePage = lazyPage(
  () => import("./pages/package/originpackage/create/index.jsx"),
  "OriginPackageCreatePage"
);
const OriginPackagePage = lazyPage(() => import("./pages/package/originpackage/Index.jsx"), "OriginPackagePage");
const OriginPackageTransportationCreatePage = lazyPage(
  () => import("./pages/package/originpackagetransportation/create/index.jsx"),
  "OriginPackageTransportationCreatePage"
);
const OriginPackageTransportationPage = lazyPage(
  () => import("./pages/package/originpackagetransportation/Index.jsx"),
  "OriginPackageTransportationPage"
);
const SalesPage = lazyPage(() => import("./pages/sales/Index.jsx"), "SalesPage");
const CreateRoleManagementPage = lazyPage(
  () => import("./pages/usermanagement/group/create"),
  "CreateRoleManagementPage"
);
const RoleManagementPage = lazyPage(() => import("./pages/usermanagement/group/Index.jsx"), "RoleManagementPage");
const CreateUserManagementPage = lazyPage(
  () => import("./pages/usermanagement/user/create"),
  "CreateUserManagementPage"
);
const UserManagementPage = lazyPage(() => import("./pages/usermanagement/user/Index.jsx"), "UserManagementPage");
const CreateWarehousePage = lazyPage(() => import("./pages/warehouse/create"), "CreateWarehousePage");
const WarehousePage = lazyPage(() => import("./pages/warehouse/Index.jsx"), "WarehousePage");

// fallback ระหว่างรอโหลด chunk ของหน้า — render ใต้ AdminLayout จึงยังเห็น sidebar ไม่ใช่จอขาวทั้งหน้า
const PageFallback = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
    <Spin size="large" />
  </div>
);

/**
 * @param deniedGoToLanding ไม่มีสิทธิ์ → พาไป "หน้าแรกที่ user เข้าได้" แทนการเตะออกจากระบบ
 *   ใช้กับ `/home` + drill-down เพราะ `/home` เป็น landing หลัง login — ถ้าเตะออก user ที่ไม่มี
 *   `Dashboard.VIEW` จะ login ไม่ได้เลย · route อื่นยังใช้พฤติกรรมเดิม (`/logout`) ไม่ถูกแตะ
 */
let PrivateRoute = ({ children, requiredPermissions, deniedGoToLanding = false }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token"); // Retrieve token from localStorage

  const { permissions, isSuperuser, groups } = useMemo(() => {
    if (!token) return { permissions: [], isSuperuser: false, groups: [] };
    try {
      const decoded = jwtDecode(token);
      return {
        permissions: Array.isArray(decoded?.permission) ? decoded.permission : [],
        isSuperuser: Boolean(decoded?.is_superuser), // แปลงเป็น Boolean เพื่อป้องกันข้อผิดพลาด
        groups: Array.isArray(decoded?.group_id) ? decoded.group_id : [],
      };
    } catch (err) {
      console.error("[PrivateRoute] Invalid JWT: ", err);
      return { permissions: [], isSuperuser: false, groups: [] };
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      console.log("No token found, redirecting to login...");
      navigate("/");
    }
    // ใช้ ?? [] เพื่อป้องกัน error ถ้า permissions เป็น undefined
    const userPermissions = permissions ?? [];
    if (!isSuperuser && requiredPermissions && requiredPermissions.length > 0) {
      /** โมดูล company: DestinationWarehouse เข้าได้เหมือน superadmin */
      const companyListBypass =
        requiredPermissions.includes(Permissions.Company.VIEW) && groups.includes(AuthGroup.DestinationWarehouse);
      /** โมดูล customer: CompanySale เข้าได้เพื่อค้นหาข้อมูลลูกค้าของ sale ตัวเอง */
      const customerCompanySaleBypass =
        requiredPermissions.includes(Permissions.Customer.VIEW) && groups.includes(AuthGroup.CompanySale);
      if (companyListBypass || customerCompanySaleBypass) {
        return;
      }
      const hasPermission = requiredPermissions.some((perm) => userPermissions.includes(perm));
      if (!hasPermission) {
        console.log("Permission denied, redirecting...");
        if (deniedGoToLanding) {
          // replace: ไม่ทิ้งหน้าที่เข้าไม่ได้ไว้ใน history (กดย้อนกลับแล้วเด้งวน)
          navigate(resolveLandingPath({ permissions: userPermissions, isSuperuser, groups }), { replace: true });
        } else {
          navigate("/logout");
        }
      }
    }
  }, [token, permissions, isSuperuser, groups, navigate, requiredPermissions, deniedGoToLanding]);

  // }, [isLoading, userData, signIn])
  return (
    <AdminLayout>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </AdminLayout>
  );
};

export const router = createBrowserRouter([
  {
    id: "root",
    path: "/",
    element: <Login />,
  },
  {
    id: "login",
    path: "/login",
    element: <Login />,
  },
  {
    // id: 'root',
    // path: '/',
    children: [
      {
        // ไม่มีสิทธิ์เมนูใดเลย → ปลายทางของ resolveLandingPath (ต้องไม่ผูก permission ไม่งั้น redirect วน)
        path: NO_ACCESS_PATH,
        element: (
          <PrivateRoute>
            <NoAccessPage />
          </PrivateRoute>
        ),
      },
      {
        // ต้องมี Dashboard.VIEW ถึงเข้าได้ (2026-08-10) · ไม่มีสิทธิ์ → พาไปหน้าแรกที่เข้าได้
        // (ห้ามใช้ /logout ที่นี่: /home เป็น landing หลัง login → user ที่ไม่มีสิทธิ์จะ login ไม่ได้เลย)
        path: "/home",
        something: true,
        element: (
          <PrivateRoute requiredPermissions={[Permissions.Dashboard.VIEW]} deniedGoToLanding>
            <Home />
          </PrivateRoute>
        ),
      },
      {
        // drill-down ของ Inbound Summary — สิทธิ์เดียวกับ /home (ข้อมูลชุดเดียวกัน)
        path: "/home/inbound-summary/company",
        element: (
          <PrivateRoute requiredPermissions={[Permissions.Dashboard.VIEW]} deniedGoToLanding>
            <DashboardInboundSummaryCompanyPage />
          </PrivateRoute>
        ),
      },
      {
        // drill-down ต่อ: ลูกค้าของบริษัทเดียว (step 2)
        path: "/home/inbound-summary/company/:companyCode",
        element: (
          <PrivateRoute requiredPermissions={[Permissions.Dashboard.VIEW]} deniedGoToLanding>
            <DashboardInboundCompanyDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: "/company",
        // something: true,
        element: (
          <PrivateRoute requiredPermissions={[Permissions.Company.VIEW]}>
            <CompanyPage />
          </PrivateRoute>
        ),
      },
      {
        path: "/customer",
        element: (
          <PrivateRoute requiredPermissions={[Permissions.Customer.VIEW]}>
            <CustomerPage />
          </PrivateRoute>
        ),
      },
      {
        path: "/company/create",
        element: (
          <PrivateRoute>
            <CompanyCreatePage requiredPermissions={[Permissions.Company.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/customer/create",
        element: (
          <PrivateRoute>
            <CustomerCreatePage requiredPermissions={[Permissions.Customer.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackage",
        element: (
          <PrivateRoute>
            <OriginPackagePage requiredPermissions={[Permissions.OriginPackage.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackage/create",
        element: (
          <PrivateRoute>
            <OriginPackageCreatePage requiredPermissions={[Permissions.OriginPackage.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackage/create/:id/:edit",
        element: (
          <PrivateRoute>
            <OriginPackageCreatePage requiredPermissions={[Permissions.OriginPackage.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackagetransportation",
        element: (
          <PrivateRoute>
            <OriginPackageTransportationPage requiredPermissions={[Permissions.OriginPackageTransportation.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/originpackagetransportation/create",
        element: (
          <PrivateRoute>
            <OriginPackageTransportationCreatePage
              requiredPermissions={[Permissions.OriginPackageTransportation.ADD]}
            />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationpackage",
        element: (
          <PrivateRoute>
            <DestinationPackagePage requiredPermissions={[Permissions.DestinationPackage.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationpackage/create",
        element: (
          <PrivateRoute>
            <DestinationPackageCreatePage requiredPermissions={[Permissions.DestinationPackage.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationdelivery",
        element: (
          <PrivateRoute>
            <DestinationDeliveryPage requiredPermissions={[Permissions.DestinationPackage.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationdelivery/create",
        element: (
          <PrivateRoute>
            <DestinationDeliveryCreatePage requiredPermissions={[Permissions.DestinationPackage.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationpackagedelivery",
        element: (
          <PrivateRoute>
            <DestinationPackageDeliveryPage requiredPermissions={[Permissions.DestinationPackageDelivery.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/package/destinationpackagedelivery/create",
        element: (
          <PrivateRoute>
            <DestinationPackageDeliveryCreatePage requiredPermissions={[Permissions.DestinationPackageDelivery.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/bill/companybill",
        element: (
          <PrivateRoute>
            <CompanyBillPage requiredPermissions={[Permissions.CompanyBill.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/bill/companybill/create",
        element: (
          <PrivateRoute>
            <CompanyBillCreatePage requiredPermissions={[Permissions.CompanyBill.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/bill/customerbill",
        element: (
          <PrivateRoute>
            <CustomerBilPage requiredPermissions={[Permissions.CustomerBill.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/bill/customerbill/create",
        element: (
          <PrivateRoute>
            <CustomerBilCreatePage requiredPermissions={[Permissions.CustomerBill.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/summary/report/K",
        element: (
          <PrivateRoute>
            <SummaryReport type="K"></SummaryReport>
          </PrivateRoute>
        ),
      },
      {
        path: "/summary/report/M",
        element: (
          <PrivateRoute>
            <SummaryReport type="M"></SummaryReport>
          </PrivateRoute>
        ),
      },
      {
        path: "/summary/report/detail",
        element: (
          <PrivateRoute>
            <SummaryReportDetail></SummaryReportDetail>
          </PrivateRoute>
        ),
      },
      {
        path: "/sales",
        element: (
          <PrivateRoute>
            <SalesPage requiredPermissions={[Permissions.Sale.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/warehouses",
        element: (
          <PrivateRoute>
            <WarehousePage requiredPermissions={[Permissions.Warehouse.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "/warehouses/create",
        element: (
          <PrivateRoute>
            <CreateWarehousePage requiredPermissions={[Permissions.Warehouse.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "usermanagement/user",
        element: (
          <PrivateRoute>
            <UserManagementPage requiredPermissions={[Permissions.User.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "usermanagement/user/create",
        element: (
          <PrivateRoute>
            <CreateUserManagementPage requiredPermissions={[Permissions.User.ADD]} />
          </PrivateRoute>
        ),
      },
      {
        path: "usermanagement/group",
        element: (
          <PrivateRoute>
            <RoleManagementPage requiredPermissions={[Permissions.Group.VIEW]} />
          </PrivateRoute>
        ),
      },
      {
        path: "usermanagement/group/create",
        element: (
          <PrivateRoute>
            <CreateRoleManagementPage requiredPermissions={[Permissions.Group.ADD]} />
          </PrivateRoute>
        ),
      },
    ],
  },
  {
    path: "/callback",
    children: [
      {
        path: "/callback/success",
        element: <Success />,
      },
      {
        path: "/callback/logout",
        element: <LogoutSuccess />,
      },
    ],
    // element: <Success />,
  },
  {
    path: "/logout",
    element: <Logout />,
  },
]);
